<template>
  <div class="user-setting-page nav-content-inset">
    <el-card class="setting-card">
      <template #header>
        <div class="card-header">
          <h2>个人朋友信息</h2>
          <el-button type="primary" @click="goEditProfile">
            <el-icon><Edit /></el-icon>
            编辑资料
          </el-button>
        </div>
      </template>

      <section class="setting-block">
        <div class="friend-feature-row">
          <span class="friend-feature-label">朋友功能</span>
          <el-switch
            v-model="friendEnabled"
            :loading="friendFeatureLoading"
            :disabled="friendFeatureLoading"
            @change="handleFriendFeatureChange"
          />
        </div>
        <p class="field-hint">开启后可被其他用户搜索，并查看其他已开启用户。个人照片满 3 张后才能开启。</p>
        <ul class="photo-check">
          <li :class="{ done: personalPhotoCount >= 1 }">第 1 张 {{ personalPhotoCount >= 1 ? '已有' : '还没有' }}</li>
          <li :class="{ done: personalPhotoCount >= 2 }">第 2 张 {{ personalPhotoCount >= 2 ? '已有' : '还没有' }}</li>
          <li :class="{ done: personalPhotoCount >= 3 }">第 3 张 {{ personalPhotoCount >= 3 ? '已有' : '还没有' }}</li>
        </ul>
        <p class="field-hint">当前 {{ Math.min(personalPhotoCount, 3) }}/3</p>
        <el-button v-if="personalPhotoCount < 3" type="primary" link @click="goEditProfile">
          去上传照片
        </el-button>
      </section>

      <section class="setting-block">
        <VisibilitySettingsPanel />
      </section>

      <section class="setting-block">
        <h3 class="block-title">资料预览</h3>
        <p class="field-hint">这是别人打开你的资料时会看到的摘要。点「别人眼中的资料」进入完整资料页。</p>
        <div class="profile-summary" v-loading="loading">
          <img v-if="preview.avatar" :src="preview.avatar" class="profile-summary__avatar" alt="" />
          <div class="profile-summary__body">
            <div class="profile-summary__name">{{ preview.nickname || '未设置昵称' }}</div>
            <div class="profile-summary__meta">
              <span>{{ genderLabel }}</span>
              <span v-if="ageLabel">{{ ageLabel }}岁</span>
              <span>{{ livePlace || '现居未填' }}</span>
              <span v-if="preview.occupation">{{ preview.occupation }}</span>
              <span v-if="preview.education">{{ preview.education }}</span>
            </div>
            <div class="profile-summary__tags" v-if="preview.tags.length">
              <el-tag v-for="t in preview.tags" :key="t" size="small" effect="plain">{{ t }}</el-tag>
            </div>
          </div>
        </div>
        <div v-if="preview.photos.length" class="preview-photos">
          <el-image
            v-for="(url, i) in preview.photos.slice(0, 6)"
            :key="url + i"
            :src="url"
            fit="cover"
            class="preview-photo"
            :preview-src-list="preview.photos"
          />
        </div>
        <div class="preview-actions">
          <el-button type="primary" plain :disabled="!previewUuid" @click="goPublicProfile">
            别人眼中的资料
          </el-button>
          <el-button @click="goEditProfile">编辑资料</el-button>
        </div>
      </section>
    </el-card>

    <el-card class="setting-card">
      <template #header>
        <div class="card-header">
          <h2>安全设置</h2>
        </div>
      </template>
      <p class="field-hint">登录名、密码、邮箱和手机绑定在账号安全页管理。</p>
      <div class="security-actions">
        <el-button type="primary" @click="goAccountSecurity">账号安全</el-button>
        <el-button @click="goAccountSecurity">修改密码</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { Edit } from '@element-plus/icons-vue'
import VisibilitySettingsPanel from '@/components/VisibilitySettingsPanel.vue'
import { photoPathToUrl, resolveProfilePhotoUrl } from '@/utils/photoUpload'

export default {
  name: 'UserSettingView',
  components: { Edit, VisibilitySettingsPanel },
  data() {
    return {
      loading: false,
      friendEnabled: false,
      friendFeatureLoading: false,
      personalPhotoCount: 0,
      previewUuid: '',
      preview: {
        nickname: '',
        avatar: '',
        gender: '',
        birthday: null,
        occupation: '',
        education: '',
        province: '',
        city: '',
        district: '',
        tags: [],
        photos: []
      }
    }
  },
  computed: {
    genderLabel() {
      return ({ male: '男', female: '女' })[this.preview.gender] || '未填性别'
    },
    ageLabel() {
      const b = this.preview.birthday
      if (!b) return ''
      const d = b instanceof Date ? b : new Date(b)
      if (Number.isNaN(d.getTime())) return ''
      const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000))
      return age > 0 && age < 120 ? String(age) : ''
    },
    livePlace() {
      return [this.preview.province, this.preview.city, this.preview.district].filter(Boolean).join(' ')
    }
  },
  created() {
    this.fetchProfile()
  },
  methods: {
    goEditProfile() {
      this.$router.push('/user/info')
    },
    goAccountSecurity() {
      this.$router.push('/user/password')
    },
    goPublicProfile() {
      if (!this.previewUuid) {
        this.$message.warning('暂时无法打开资料预览')
        return
      }
      this.$router.push(`/profile/${this.previewUuid}`)
    },
    async fetchProfile() {
      this.loading = true
      try {
        const token = localStorage.getItem('jwt_token')
        const uuid = localStorage.getItem('uuid')
        const response = await fetch('/api/v1/user/myprofile/get', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ uuid })
        })
        const data = await response.json()
        if (data.code !== 200 || !data.data) {
          this.$message.error(data.message || '获取资料失败')
          return
        }
        const row = data.data
        const photos = (row.photos || [])
          .filter((photo) => photo && String(photo).trim() !== '')
          .map((photo) => photoPathToUrl(photo))
        const birthday = row.birthday
          ? new Date(
              Math.floor(row.birthday / 10000),
              Math.floor((row.birthday % 10000) / 100) - 1,
              row.birthday % 100
            )
          : null
        this.previewUuid = row.uuid || uuid || ''
        this.friendEnabled = !!row.friendEnabled
        this.personalPhotoCount = photos.length
        this.preview = {
          nickname: row.nickname || '',
          avatar: resolveProfilePhotoUrl(row.avatar, row.photo),
          gender: row.sex || '',
          birthday,
          occupation: row.occupation || '',
          education: row.education || '',
          province: row.province || '',
          city: row.city || '',
          district: row.district || '',
          tags: (row.tags || []).filter(Boolean),
          photos
        }
      } catch (error) {
        console.error(error)
        this.$message.error('获取资料失败')
      } finally {
        this.loading = false
      }
    },
    async handleFriendFeatureChange(enabled) {
      if (enabled && this.personalPhotoCount < 3) {
        this.friendEnabled = false
        this.$message.warning('开启朋友功能需要至少3张个人照片，请先去编辑资料上传并保存')
        return
      }
      const prev = !enabled
      this.friendFeatureLoading = true
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/user/friend/feature', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ enabled })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.friendEnabled = !!data.data?.friendEnabled
          this.$message.success(enabled ? '朋友功能已开启' : '朋友功能已关闭')
        } else {
          this.friendEnabled = prev
          this.$message.error(data.message || '操作失败')
        }
      } catch (error) {
        this.friendEnabled = prev
        this.$message.error('操作失败')
      } finally {
        this.friendFeatureLoading = false
      }
    }
  }
}
</script>

<style scoped>
.user-setting-page {
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

.setting-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  margin: 0;
  font-size: 20px;
}

.setting-block {
  margin-bottom: 24px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.setting-block:last-child {
  margin-bottom: 0;
}

.block-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.friend-feature-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.friend-feature-label {
  font-weight: 600;
  color: #303133;
}

.field-hint {
  margin: 6px 0 10px;
  font-size: 12px;
  color: #909399;
  line-height: 1.45;
}

.photo-check {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #909399;
}

.photo-check .done {
  color: #67c23a;
}

.profile-summary {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 14px 16px;
  margin: 8px 0 12px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 10px;
}

.profile-summary__avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  background: #fff;
}

.profile-summary__name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.profile-summary__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 6px;
  font-size: 13px;
  color: #606266;
}

.profile-summary__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.preview-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.preview-photo {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
}

.preview-actions,
.security-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

@media (max-width: 767px) {
  .user-setting-page {
    padding: 0;
  }

  .profile-summary {
    align-items: flex-start;
  }
}
</style>
